import { Inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse, HttpErrorResponse, HttpClient } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { Router, NavigationEnd } from '@angular/router';
import { MasterDataSeriveService } from './master-data-serive.service';
import { UserserviceService } from './userservice.service';
import { environment } from '../environments/environment';
import { CookieService } from 'ngx-cookie-service';
import { CommonServiceService } from './common-service.service';
import { ErrorHandlerService } from './error-handler.service';

@Injectable()
export class Interceptor implements HttpInterceptor {

  private isRefreshing = false;
  private isLoggingOut = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  private currentUrl: string = '';

  // Centralize auth page routes
  private readonly AUTH_ROUTES: string[] = ['/shared/login', '/login', '/'];
  private readonly AUTH_ROUTE_PREFIXES: string[] = [
    '/shared/reset-password',
    '/shared/forget-password',
    '/shared/complete-account-setup',
  ];

  constructor(
    private userService: UserserviceService,
    private router: Router,
    private http: HttpClient,
    private cookie: CookieService,
    private commonService: CommonServiceService,
    @Inject('BASE_URL') private baseUrl: string,
    private masterService: MasterDataSeriveService,
    private errorHandler: ErrorHandlerService
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = event.url;
      }
    });
  }

  // Check if we are currently on an auth-related page
  private isOnAuthPage(): boolean {
    const url = this.currentUrl.split('?')[0].split('#')[0];
    return (
      this.AUTH_ROUTES.includes(url) ||
      this.AUTH_ROUTE_PREFIXES.some((prefix) => url.startsWith(prefix))
    );
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.url.includes('https://api.cognitive.microsofttranslator.com/')) {
      return next.handle(request);
    }

    let authToken = this.userService.getAuthToken();
    if (authToken != null) {
      request = request.clone({
        setHeaders: {
          Authorization: `Bearer ${authToken}`
        }
      })
    }

    const isAuthPage = this.isOnAuthPage();

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        if ((error.status === 401)) {
          return this.handle401Error(request, next, isAuthPage);
        }

        if ((error.status === 403)) {
          this.router.navigateByUrl(environment.companyWebBaseUrl);
          localStorage.setItem('isUserSessionEnd', JSON.stringify('showPopUp'));
        } else {
          // Handle all other errors using the error handler service
          this.errorHandler.getErrorMessage(error);
        }
        return throwError(error);
      })
    );
  }

  private handle401Error(
    req: HttpRequest<any>,
    next: HttpHandler,
    isAuthPage: boolean
  ): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.cookie.get('refreshToken');
      if (refreshToken) {
        // Use environment API URL instead of hardcoded path
        const refreshUrl = `${environment.companyApiBaseUrl}/auth/refresh`;
        return this.http.post<any>(refreshUrl, { refreshToken }).pipe(
          switchMap((response: any) => {
            this.isRefreshing = false;
            this.setAccessToken(response.accessToken);
            this.refreshTokenSubject.next(response.accessToken);

            return next.handle(this.addToken(req, response.accessToken));
          }),
          catchError((err) => {
            this.isRefreshing = false;
            this.clearAuthCookies();
            if (!isAuthPage) {
              this.logOut(true);
            }
            return throwError(() => err);
          })
        );
      } else {
        this.isRefreshing = false;
        this.clearAuthCookies();
        if (!isAuthPage) {
          this.logOut(true);
        }
        return throwError(() => new Error('Refresh token is not available'));
      }
    } else {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1),
        switchMap((jwt) => {
          return next.handle(this.addToken(req, jwt));
        })
      );
    }
  }

  private setAccessToken(token: string) {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      this.cookie.set('accessToken', token, undefined, '/', undefined, true, 'None');
    } else {
      this.cookie.set('accessToken', token, undefined, '/', `.${environment.domainName}`, true, 'None');
    }
  }

  private clearAuthCookies() {
    this.cookie.delete('accessToken', '/', `.${environment.domainName}`);
    this.cookie.delete('refreshToken', '/', `.${environment.domainName}`);
  }

  private addToken(request: HttpRequest<any>, token: string): HttpRequest<any> {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  // isAuthFailure: true for dead-session logouts (from handle401Error), where the
  // unassign call can't authenticate and would only spam backend 401 logs — skip it.
  logOut(isAuthFailure: boolean = false){
    // Guard against re-entry: the unassign request below can 401 and re-trigger
    // logOut(), looping forever. Not reset since performLogout() navigates away.
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    const domain = environment.domainName;
        const companyweburl = environment.companyWebBaseUrl;

        const performLogout = () => {
          localStorage.clear();
          sessionStorage.clear();
          this.cookie.deleteAll('/', domain);
          document.cookie = `logoutEvent=1; domain=${domain}; path=/; max-age=10`;
          window.location.href = `${companyweburl}/shared/login`;
        };

        const isBypassLicense =
          sessionStorage.getItem('isBypassLicense') === 'true';
        if (isBypassLicense || isAuthFailure) {
          performLogout();
        } else {
          // performLogout on both success and error so the redirect always happens.
          this.commonService.unassignStationLicenseAndContinue(performLogout, performLogout);
        }
  }

}
